import React, { useEffect, useState } from 'react';
import { apiCall } from '../AuthContext';
import { Save, Loader2, CheckCircle, AlertCircle, Wifi, WifiOff, ChevronDown, MapPin, ExternalLink } from 'lucide-react';
import { SquareLogo, ShopifyLogo, EbayLogo } from '../components/BrandLogos';

export default function Configuration() {
  const [config, setConfig] = useState({
    square_token: '', square_location_id: '', square_location_name: '', store_name: '',
    shopify_store: '', shopify_token: '', shopify_location_id: '',
    ebay_oauth_token: '', ebay_environment: 'SANDBOX'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [squareTesting, setSquareTesting] = useState(false);
  const [shopifyTesting, setShopifyTesting] = useState(false);
  const [ebayTesting, setEbayTesting] = useState(false);
  const [squareStatus, setSquareStatus] = useState(null);
  const [shopifyStatus, setShopifyStatus] = useState(null);
  const [ebayStatus, setEbayStatus] = useState(null);
  const [squareLocations, setSquareLocations] = useState([]);
  const [shopifyLocations, setShopifyLocations] = useState([]);
  const [shopifyConnecting, setShopifyConnecting] = useState(false);
  const [squareConnecting, setSquareConnecting] = useState(false);
  const [ebayConnecting, setEbayConnecting] = useState(false);
  const [reconnectHint, setReconnectHint] = useState(null); // 'shopify' | null
  const [shopifyScope, setShopifyScope] = useState(null); // {granted_scopes, missing_scopes, needs_reconnect, connected_at}

  const fetchShopifyScope = async () => {
    try {
      const r = await apiCall('/api/shopify/scope-status', { method: 'GET' });
      setShopifyScope(r.data);
    } catch (_e) { setShopifyScope(null); }
  };

  useEffect(() => {
    apiCall('/api/configuration', { method: 'GET' })
      .then(r => { if (r.data && Object.keys(r.data).length) setConfig(prev => ({ ...prev, ...r.data })); })
      .catch(() => {})
      .finally(() => setLoading(false));
    fetchShopifyScope();
    
    // Check URL params for OAuth callbacks
    const params = new URLSearchParams(window.location.search);
    // Friendly explanations for the new error codes the OAuth callbacks emit.
    const friendlyOAuthError = (provider, code) => {
      const c = (code || '').toLowerCase();
      if (c === 'access_denied' || c === 'user_cancelled') {
        return `${provider} connection cancelled — you clicked Cancel/Decline on the consent screen. Click Connect again when you're ready.`;
      }
      if (c === 'missing_code') {
        return `${provider} returned no authorization code. This usually means the install was blocked (organization restriction, app not approved, or wrong store). Check your developer-portal app distribution settings.`;
      }
      if (c === 'invalid_state') {
        return `${provider} OAuth session expired or was tampered with. Click Connect again to start a fresh session.`;
      }
      if (c === 'token_exchange_failed') {
        return `${provider} accepted authorization but rejected the token exchange. Most common cause: your client secret in the app's developer portal doesn't match what's in our server config. Contact support.`;
      }
      if (c === 'no_token') {
        return `${provider} returned an empty access token. Try Connect again, then contact support if it persists.`;
      }
      return `${provider} connection failed: ${code}`;
    };

    if (params.get('shopify_connected') === 'true') {
      setShopifyStatus({ success: true, message: 'Shopify connected successfully via OAuth!' });
      // Refresh config + scope to reflect the fresh token/scope
      apiCall('/api/configuration', { method: 'GET' })
        .then(r => { if (r.data && Object.keys(r.data).length) setConfig(prev => ({ ...prev, ...r.data })); })
        .catch(() => {});
      fetchShopifyScope();
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('shopify_error')) {
      setShopifyStatus({ success: false, message: friendlyOAuthError('Shopify', params.get('shopify_error')) });
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('square_connected') === 'true') {
      setSquareStatus({ success: true, message: 'Square connected successfully via OAuth!' });
      apiCall('/api/configuration', { method: 'GET' })
        .then(r => { if (r.data && Object.keys(r.data).length) setConfig(prev => ({ ...prev, ...r.data })); })
        .catch(() => {});
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('square_error')) {
      setSquareStatus({ success: false, message: friendlyOAuthError('Square', params.get('square_error')) });
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('ebay_connected') === 'true') {
      setEbayStatus({ success: true, message: 'eBay connected successfully via OAuth!' });
      apiCall('/api/configuration', { method: 'GET' })
        .then(r => { if (r.data && Object.keys(r.data).length) setConfig(prev => ({ ...prev, ...r.data })); })
        .catch(() => {});
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('ebay_error')) {
      setEbayStatus({ success: false, message: friendlyOAuthError('eBay', params.get('ebay_error')) });
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('reconnect') === 'shopify') {
      setReconnectHint('shopify');
      // Scroll to Shopify section after render
      setTimeout(() => {
        const el = document.querySelector('[data-section="shopify"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await apiCall('/api/configuration', { method: 'POST', data: config });
      setMessage({ type: 'success', text: 'Configuration saved successfully!' });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Failed to save' });
    } finally { setSaving(false); }
  };

  const testSquare = async () => {
    setSquareTesting(true); setSquareStatus(null); setSquareLocations([]);
    try {
      // Save first so backend has the latest token
      await apiCall('/api/configuration', { method: 'POST', data: config });
      const res = await apiCall('/api/test-square-connection', { method: 'POST' });
      setSquareStatus(res.data);
      if (res.data.success && res.data.locations) {
        setSquareLocations(res.data.locations);
        // Auto-select first location if none set
        if (res.data.locations.length > 0) {
          const loc = config.square_location_id
            ? res.data.locations.find(l => l.location_id === config.square_location_id) || res.data.locations[0]
            : res.data.locations[0];
          const updated = { ...config, square_location_id: loc.location_id, square_location_name: loc.location_name };
          setConfig(updated);
          // Auto-save with location
          await apiCall('/api/configuration', { method: 'POST', data: updated });
        }
      }
    } catch (e) {
      setSquareStatus({ success: false, message: e.response?.data?.detail || 'Connection failed' });
    } finally { setSquareTesting(false); }
  };

  const connectSquare = async () => {
    setSquareConnecting(true); setSquareStatus(null);
    try {
      const res = await apiCall('/api/square/auth', { method: 'GET' });
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (e) {
      setSquareStatus({ success: false, message: e.response?.data?.detail || 'Failed to start Square connection.' });
      setSquareConnecting(false);
    }
  };

  const testShopify = async () => {
    setShopifyTesting(true); setShopifyStatus(null); setShopifyLocations([]);
    try {
      // Auto-format store name: lowercase, replace spaces with hyphens, strip .myshopify.com
      let storeName = config.shopify_store.trim().toLowerCase()
        .replace(/\.myshopify\.com$/i, '')
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
      const updatedConfig = { ...config, shopify_store: storeName };
      setConfig(updatedConfig);
      await apiCall('/api/configuration', { method: 'POST', data: updatedConfig });
      const res = await apiCall('/api/test-shopify-connection', { method: 'POST' });
      setShopifyStatus(res.data);
      if (res.data.success && res.data.locations) {
        setShopifyLocations(res.data.locations);
        if (res.data.locations.length > 0) {
          const loc = config.shopify_location_id
            ? res.data.locations.find(l => l.id === config.shopify_location_id) || res.data.locations[0]
            : res.data.locations[0];
          const withLoc = { ...updatedConfig, shopify_location_id: loc.id };
          setConfig(withLoc);
          await apiCall('/api/configuration', { method: 'POST', data: withLoc });
        }
      }
    } catch (e) {
      setShopifyStatus({ success: false, message: e.response?.data?.detail || 'Connection failed' });
    } finally { setShopifyTesting(false); }
  };

  const connectShopify = async () => {
    if (!config.shopify_store) {
      setShopifyStatus({ success: false, message: 'Please enter your Shopify store name first.' });
      return;
    }
    setShopifyConnecting(true); setShopifyStatus(null);
    try {
      const storeName = config.shopify_store.trim().toLowerCase()
        .replace(/\.myshopify\.com$/i, '')
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
      const res = await apiCall(`/api/shopify/auth?shop=${encodeURIComponent(storeName)}`, { method: 'GET' });
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (e) {
      setShopifyStatus({ success: false, message: e.response?.data?.detail || 'Failed to start Shopify connection.' });
      setShopifyConnecting(false);
    }
  };

  const testEbay = async () => {
    setEbayTesting(true); setEbayStatus(null);
    try {
      await apiCall('/api/configuration', { method: 'POST', data: config });
      const res = await apiCall('/api/test-ebay-connection', { method: 'POST' });
      setEbayStatus(res.data);
    } catch (e) {
      setEbayStatus({ success: false, message: e.response?.data?.detail || 'Connection failed' });
    } finally { setEbayTesting(false); }
  };

  const connectEbay = async () => {
    setEbayConnecting(true); setEbayStatus(null);
    try {
      const res = await apiCall('/api/ebay/auth', { method: 'GET' });
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch (e) {
      setEbayStatus({ success: false, message: e.response?.data?.detail || 'Failed to start eBay connection.' });
      setEbayConnecting(false);
    }
  };

  const disconnectIntegration = async (provider) => {
    if (!window.confirm(`Disconnect ${provider}? You'll need to reconnect to push products.`)) return;
    try {
      await apiCall(`/api/disconnect-${provider.toLowerCase()}`, { method: 'POST' });
      // Clear local state immediately
      if (provider === 'ebay') {
        setConfig(prev => ({ ...prev, ebay_oauth_token: '', ebay_environment: 'SANDBOX' }));
        setEbayStatus(null);
      }
      if (provider === 'shopify') {
        setConfig(prev => ({ ...prev, shopify_token: '', shopify_store: '', shopify_location_id: '' }));
        setShopifyStatus(null);
      }
      if (provider === 'square') {
        setConfig(prev => ({ ...prev, square_token: '', square_location_id: '', square_location_name: '' }));
        setSquareStatus(null);
      }
      setMessage({ type: 'success', text: `${provider} disconnected.` });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || `Failed to disconnect ${provider}.` });
    }
  };

  const handleLocationChange = async (field, value, nameField, locations, idKey, nameKey) => {
    const loc = locations.find(l => l[idKey] === value);
    const updated = { ...config, [field]: value };
    if (loc && nameField) updated[nameField] = loc[nameKey];
    setConfig(updated);
    // Auto-save location change
    try { await apiCall('/api/configuration', { method: 'POST', data: updated }); } catch {}
  };

  const handleChange = (field, value) => setConfig(p => ({ ...p, [field]: value }));

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin text-[#0052FF]" />
    </div>
  );

  return (
    <div className="space-y-6 max-w-4xl" data-testid="configuration-page">
      <div>
        <h2 className="text-2xl font-bold text-[#0F172A]">Integrations</h2>
        <p className="text-sm text-slate-500 mt-1">Connect Square POS, Shopify, and eBay. Use <strong>Test Connection</strong> to verify your tokens.</p>
      </div>

      {message && (
        <div className={`flex items-center gap-2 p-4 rounded-xl border ${message.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`} data-testid="config-message">
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          <p className="text-sm font-medium">{message.text}</p>
        </div>
      )}

      {/* Square POS */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="square-config">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center">
            <SquareLogo className="w-10 h-10" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-[#0F172A]">Square POS</h3>
            <p className="text-xs text-slate-500">{config.square_token && config.store_name ? <><span className="text-green-600 font-medium">{config.store_name}</span>{config.square_location_name ? ` — ${config.square_location_name}` : ''}</> : 'Connect your Square account to push products directly'}</p>
          </div>
          {config.square_token && <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Connected</span>}
        </div>

        {/* Connect / Disconnect */}
        <div className="mb-5 p-4 bg-slate-50 border border-slate-200 rounded-xl">
          {config.square_token ? (
            <>
              <p className="text-sm font-medium text-green-700 mb-2">Square Account Connected</p>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-[#0F172A] mb-1">Store Name</label>
                  <input value={config.store_name} onChange={e => handleChange('store_name', e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-slate-400 focus:border-transparent transition-all outline-none text-sm"
                    placeholder="My Store" data-testid="square-store-name-input" />
                </div>
                <button onClick={connectSquare} disabled={squareConnecting}
                  className="inline-flex items-center gap-2 bg-black hover:bg-slate-800 text-white font-semibold rounded-lg py-2.5 px-6 text-sm transition-colors disabled:opacity-50 whitespace-nowrap"
                  data-testid="connect-square-btn">
                  {squareConnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <SquareLogo className="w-4 h-4" />}
                  Reconnect
                </button>
                <button onClick={() => disconnectIntegration('square')}
                  className="inline-flex items-center gap-2 bg-white hover:bg-red-50 text-red-600 border border-red-300 font-medium rounded-lg py-2.5 px-6 text-sm transition-colors whitespace-nowrap"
                  data-testid="disconnect-square-btn">
                  Disconnect
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-[#0F172A] mb-2">Quick Connect</p>
              <p className="text-xs text-slate-500 mb-3">Click Connect to sign in to your Square account and authorize access automatically.</p>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-[#0F172A] mb-1">Store Name</label>
                  <input value={config.store_name} onChange={e => handleChange('store_name', e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-slate-400 focus:border-transparent transition-all outline-none text-sm"
                    placeholder="My Store" data-testid="square-store-name-input" />
                </div>
                <button onClick={connectSquare} disabled={squareConnecting}
                  className="inline-flex items-center gap-2 bg-black hover:bg-slate-800 text-white font-semibold rounded-lg py-2.5 px-6 text-sm transition-colors disabled:opacity-50 whitespace-nowrap"
                  data-testid="connect-square-btn">
                  {squareConnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <SquareLogo className="w-4 h-4" />}
                  {squareConnecting ? 'Connecting...' : 'Connect Square'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Manual token (advanced) */}
        <details className="group">
          <summary className="cursor-pointer text-xs font-medium text-slate-400 hover:text-slate-600 mb-3 flex items-center gap-1" data-testid="square-manual-toggle">
            <ChevronDown className="w-3.5 h-3.5 group-open:rotate-180 transition-transform" /> Advanced: Manual Token Setup
          </summary>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Access Token</label>
              <input value={config.square_token} onChange={e => handleChange('square_token', e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                placeholder="EAAAl..." data-testid="square-token-input" type="password" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1.5">
                <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Location</span>
              </label>
              {squareLocations.length > 0 ? (
                <select value={config.square_location_id} onChange={e => {
                  handleLocationChange('square_location_id', e.target.value, 'square_location_name', squareLocations, 'location_id', 'location_name');
                }}
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                  data-testid="square-location-select">
                  <option value="">Select location...</option>
                  {squareLocations.map(loc => (
                    <option key={loc.location_id} value={loc.location_id}>{loc.location_name}</option>
                  ))}
                </select>
              ) : (
                <input value={config.square_location_id} onChange={e => handleChange('square_location_id', e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                  placeholder="Test connection to auto-fetch" data-testid="square-location-id-input" />
              )}
            </div>
            <div className="sm:col-span-2">
              <button onClick={testSquare} disabled={squareTesting || !config.square_token}
                className="inline-flex items-center justify-center gap-2 bg-[#0F172A] hover:bg-slate-800 text-white font-medium rounded-lg py-2.5 px-6 text-sm transition-colors disabled:opacity-50"
                data-testid="test-square-btn">
                {squareTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
                {squareTesting ? 'Testing...' : 'Test Connection'}
              </button>
            </div>
          </div>
        </details>

        {squareStatus && (
          <div className={`mt-4 p-3 rounded-lg text-sm ${squareStatus.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}
            data-testid="square-test-result">
            {squareStatus.success ? <CheckCircle className="w-4 h-4 inline mr-1.5" /> : <WifiOff className="w-4 h-4 inline mr-1.5" />}
            {squareStatus.message}
          </div>
        )}
      </div>

      {/* Shopify */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="shopify-config" data-section="shopify">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center">
            <ShopifyLogo className="w-10 h-10" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-[#0F172A]">Shopify</h3>
            <p className="text-xs text-slate-500">{config.shopify_token && config.shopify_store ? <><span className="text-green-600 font-medium">{config.shopify_store}.myshopify.com</span></> : 'Connect your Shopify store to sync products and inventory'}</p>
          </div>
          {config.shopify_token && <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Connected</span>}
          {!config.shopify_token && shopifyStatus && (
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${shopifyStatus.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {shopifyStatus.success ? 'Connected' : 'Failed'}
            </span>
          )}
        </div>

        {reconnectHint === 'shopify' && (
          <div className="mb-5 p-4 rounded-xl bg-amber-50 border-2 border-amber-300" data-testid="shopify-reconnect-hint">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-bold text-amber-900">Reconnect required — missing Shopify scopes</div>
                {shopifyScope && (
                  <div className="mt-2 text-xs text-amber-900 space-y-1">
                    <div>Last connected: <strong>{shopifyScope.connected_at ? new Date(shopifyScope.connected_at).toLocaleString() : '—'}</strong></div>
                    <div>
                      <span className="font-semibold">Granted ({shopifyScope.granted_scopes.length}):</span>{' '}
                      <code className="bg-white/70 px-1 rounded text-[10px]">{shopifyScope.granted_scopes.join(', ') || '<none>'}</code>
                    </div>
                    {shopifyScope.missing_scopes.length > 0 && (
                      <div>
                        <span className="font-semibold text-red-700">Missing ({shopifyScope.missing_scopes.length}):</span>{' '}
                        <code className="bg-red-50 text-red-800 px-1 rounded text-[10px]">{shopifyScope.missing_scopes.join(', ')}</code>
                      </div>
                    )}
                  </div>
                )}
                <div className="text-xs text-amber-800 mt-2 leading-relaxed">
                  <strong>Steps that work reliably:</strong>
                  <ol className="list-decimal ml-5 mt-1 space-y-0.5">
                    <li>Click <strong>Disconnect</strong> below (wipes the stale token).</li>
                    <li>Click <strong>Connect Shopify</strong>. A Shopify login/consent tab will open.</li>
                    <li>On the Shopify consent screen, verify the missing permissions above are listed, then click <strong>Install app</strong>.</li>
                    <li>You'll be redirected back here with the new scopes granted.</li>
                  </ol>
                  If the consent screen doesn't list the missing scopes, your Shopify Partners app config needs them declared first (Partners Dashboard → App Setup → API Access → App Scopes).
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Connect / Disconnect */}
        <div className="mb-5 p-4 bg-[#f4f9ee] border border-[#96bf48]/30 rounded-xl">
          {config.shopify_token ? (
            <>
              <p className="text-sm font-medium text-green-700 mb-2">Shopify Store Connected</p>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-[#0F172A] mb-1">Store Name *</label>
                  <input value={config.shopify_store} onChange={e => handleChange('shopify_store', e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#96bf48] focus:border-transparent transition-all outline-none text-sm"
                    placeholder="your-store (without .myshopify.com)" data-testid="shopify-store-input" />
                </div>
                <button onClick={connectShopify} disabled={shopifyConnecting || !config.shopify_store}
                  className="inline-flex items-center gap-2 bg-[#96bf48] hover:bg-[#7da33d] text-white font-semibold rounded-lg py-2.5 px-6 text-sm transition-colors disabled:opacity-50 whitespace-nowrap"
                  data-testid="connect-shopify-btn">
                  {shopifyConnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShopifyLogo className="w-4 h-4" />}
                  Reconnect
                </button>
                <button onClick={() => disconnectIntegration('shopify')}
                  className="inline-flex items-center gap-2 bg-white hover:bg-red-50 text-red-600 border border-red-300 font-medium rounded-lg py-2.5 px-6 text-sm transition-colors whitespace-nowrap"
                  data-testid="disconnect-shopify-btn">
                  Disconnect
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-[#0F172A] mb-2">Quick Connect</p>
              <p className="text-xs text-slate-500 mb-3">Enter your store name and click Connect — you'll be redirected to Shopify to authorize access.</p>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-[#0F172A] mb-1">Store Name *</label>
                  <input value={config.shopify_store} onChange={e => handleChange('shopify_store', e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#96bf48] focus:border-transparent transition-all outline-none text-sm"
                    placeholder="your-store (without .myshopify.com)" data-testid="shopify-store-input" />
                </div>
                <button onClick={connectShopify} disabled={shopifyConnecting || !config.shopify_store}
                  className="inline-flex items-center gap-2 bg-[#96bf48] hover:bg-[#7da33d] text-white font-semibold rounded-lg py-2.5 px-6 text-sm transition-colors disabled:opacity-50 whitespace-nowrap"
                  data-testid="connect-shopify-btn">
                  {shopifyConnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShopifyLogo className="w-4 h-4" />}
                  {shopifyConnecting ? 'Connecting...' : 'Connect Shopify'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Manual token (advanced) */}
        <details className="group">
          <summary className="cursor-pointer text-xs font-medium text-slate-400 hover:text-slate-600 mb-3 flex items-center gap-1" data-testid="shopify-manual-toggle">
            <ChevronDown className="w-3.5 h-3.5 group-open:rotate-180 transition-transform" /> Advanced: Manual Token Setup
          </summary>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Access Token</label>
              <input value={config.shopify_token} onChange={e => handleChange('shopify_token', e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                placeholder="shpat_..." data-testid="shopify-token-input" type="password" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1.5">
                <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Location</span>
              </label>
              {shopifyLocations.length > 0 ? (
                <select value={config.shopify_location_id} onChange={e => {
                  handleLocationChange('shopify_location_id', e.target.value, null, shopifyLocations, 'id', 'name');
                }}
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                  data-testid="shopify-location-select">
                  <option value="">Select location...</option>
                  {shopifyLocations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              ) : (
                <input value={config.shopify_location_id} onChange={e => handleChange('shopify_location_id', e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                  placeholder="Test connection to auto-fetch" data-testid="shopify-location-input" />
              )}
            </div>
            <div className="sm:col-span-2">
              <button onClick={testShopify} disabled={shopifyTesting || !config.shopify_token || !config.shopify_store}
                className="inline-flex items-center justify-center gap-2 bg-[#0F172A] hover:bg-slate-800 text-white font-medium rounded-lg py-2.5 px-6 text-sm transition-colors disabled:opacity-50"
                data-testid="test-shopify-btn">
                {shopifyTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
                {shopifyTesting ? 'Testing...' : 'Test Connection'}
              </button>
            </div>
          </div>
        </details>

        {shopifyStatus && (
          <div className={`mt-4 p-3 rounded-lg text-sm ${shopifyStatus.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}
            data-testid="shopify-test-result">
            {shopifyStatus.success ? <CheckCircle className="w-4 h-4 inline mr-1.5" /> : <WifiOff className="w-4 h-4 inline mr-1.5" />}
            {shopifyStatus.message}
          </div>
        )}
      </div>

      {/* eBay */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="ebay-config">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center">
            <EbayLogo className="w-10 h-10" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-[#0F172A]">eBay</h3>
            <p className="text-xs text-slate-500">{config.ebay_oauth_token ? <><span className="text-green-600 font-medium">eBay Seller Account</span>{config.ebay_environment === 'PRODUCTION' ? ' (Production)' : ' (Sandbox)'}</> : 'Connect your eBay seller account to list products directly'}</p>
          </div>
          {config.ebay_oauth_token && <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">Connected</span>}
          {!config.ebay_oauth_token && ebayStatus && (
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${ebayStatus.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {ebayStatus.success ? 'Connected' : 'Failed'}
            </span>
          )}
        </div>

        {/* Connect / Disconnect */}
        <div className="mb-5 p-4 bg-red-50/50 border border-[#e53238]/20 rounded-xl">
          {config.ebay_oauth_token ? (
            <>
              <p className="text-sm font-medium text-green-700 mb-2">eBay Account Connected</p>
              <div className="flex gap-3">
                <button onClick={connectEbay} disabled={ebayConnecting}
                  className="inline-flex items-center gap-2 bg-[#e53238] hover:bg-[#cc2d32] text-white font-semibold rounded-lg py-2.5 px-6 text-sm transition-colors disabled:opacity-50"
                  data-testid="connect-ebay-btn">
                  {ebayConnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <EbayLogo className="w-4 h-4" />}
                  Reconnect
                </button>
                <button onClick={() => disconnectIntegration('ebay')}
                  className="inline-flex items-center gap-2 bg-white hover:bg-red-50 text-red-600 border border-red-300 font-medium rounded-lg py-2.5 px-6 text-sm transition-colors"
                  data-testid="disconnect-ebay-btn">
                  Disconnect
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-[#0F172A] mb-2">Quick Connect</p>
              <p className="text-xs text-slate-500 mb-3">Click Connect to sign in to your eBay account and authorize access automatically.</p>
              <button onClick={connectEbay} disabled={ebayConnecting}
                className="inline-flex items-center gap-2 bg-[#e53238] hover:bg-[#cc2d32] text-white font-semibold rounded-lg py-2.5 px-6 text-sm transition-colors disabled:opacity-50"
                data-testid="connect-ebay-btn">
                {ebayConnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <EbayLogo className="w-4 h-4" />}
                {ebayConnecting ? 'Connecting...' : 'Connect eBay'}
              </button>
            </>
          )}
        </div>

        {/* Manual token (advanced) */}
        <details className="group">
          <summary className="cursor-pointer text-xs font-medium text-slate-400 hover:text-slate-600 mb-3 flex items-center gap-1" data-testid="ebay-manual-toggle">
            <ChevronDown className="w-3.5 h-3.5 group-open:rotate-180 transition-transform" /> Advanced: Manual Token Setup
          </summary>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1.5">OAuth User Token</label>
              <input value={config.ebay_oauth_token} onChange={e => handleChange('ebay_oauth_token', e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                placeholder="v^1.1#i^1#p^3#..." data-testid="ebay-token-input" type="password" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Environment</label>
              <select value={config.ebay_environment} onChange={e => handleChange('ebay_environment', e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#0052FF] focus:border-transparent transition-all outline-none text-sm"
                data-testid="ebay-env-select">
                <option value="SANDBOX">Sandbox (Testing)</option>
                <option value="PRODUCTION">Production (Live)</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <button onClick={testEbay} disabled={ebayTesting || !config.ebay_oauth_token}
                className="inline-flex items-center justify-center gap-2 bg-[#0F172A] hover:bg-slate-800 text-white font-medium rounded-lg py-2.5 px-6 text-sm transition-colors disabled:opacity-50"
                data-testid="test-ebay-btn">
                {ebayTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
                {ebayTesting ? 'Testing...' : 'Test Connection'}
              </button>
            </div>
          </div>
        </details>

        {ebayStatus && (
          <div className={`mt-4 p-3 rounded-lg text-sm ${ebayStatus.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}
            data-testid="ebay-test-result">
            {ebayStatus.success ? <CheckCircle className="w-4 h-4 inline mr-1.5" /> : <WifiOff className="w-4 h-4 inline mr-1.5" />}
            {ebayStatus.message}
          </div>
        )}
      </div>

      {/* Save */}
      <button onClick={handleSave} disabled={saving} data-testid="save-config-button"
        className="inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium rounded-lg px-6 py-2.5 text-sm transition-colors disabled:opacity-50">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {saving ? 'Saving...' : 'Save Configuration'}
      </button>
    </div>
  );
}
